import { configureStore } from "@reduxjs/toolkit";
import {
    createMigrate,
    persistReducer,
    persistStore,
    FLUSH,
    REHYDRATE,
    PAUSE,
    PERSIST,
    PURGE,
    REGISTER,
} from "redux-persist";
import storage from "redux-persist/lib/storage";

import rootReducer from "./rootReducer";
import { setDraftOwner, sweepExpiredDrafts } from "../hooks/useAutoSave.js";

// The access token must NEVER be written to localStorage — that's the whole
// point of moving the refresh token to an HttpOnly cookie. We strip it
// on the way in (writing to storage) and force it to null on the way out
// (rehydrating). On app start, the first API call 401s and the interceptor
// silently refreshes via the cookie.
const authTransform = {
    in: (state, key) => {
        if (key !== "auth" || !state) return state;
        const { token, ...rest } = state;
        return rest;
    },
    out: (state, key) => {
        if (key !== "auth" || !state) return state;
        return { ...state, token: null };
    },
};

// Versioned persisted state. Bump `version` and add a step to `migrations`
// whenever the shape of `auth` or `permissions` changes, so a browser holding
// the old shape is upgraded on load instead of rehydrating stale data.
// v1 (2026-10): first version — keep everything, only repair the shape.
export const migrations = {
    1: (state) => {
        if (!state) return state;
        const permissions = state.permissions && Array.isArray(state.permissions.list)
            ? state.permissions
            : { ...(state.permissions || {}), list: [] };
        const auth = state.auth && typeof state.auth === "object"
            ? { refreshExpiresAt: null, user: null, ...state.auth, token: null }
            : state.auth;
        return { ...state, auth, permissions };
    },
};

const persistConfig = {
    key: "root",
    version: 1,
    storage,
    whitelist: ["auth", "permissions"],
    transforms: [authTransform],
    migrate: createMigrate(migrations, { debug: false }),
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const store = configureStore({
    reducer: persistedReducer,
    // redux-persist's PERSIST/REHYDRATE/etc. actions carry non-serializable
    // functions; whitelist them so RTK's dev-only serializability check stays
    // quiet and real warnings remain visible.
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({
            serializableCheck: {
                ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
            },
        }),
});

export const persistor = persistStore(store);

// Auto-save drafts are namespaced per user and kept across logout / session
// expiry so the same user can restore them after signing in again; drafts
// older than 30 days are swept at startup.
sweepExpiredDrafts();
const userKey = (st) => {
    const u = st.auth?.user;
    return u ? (u.userid ?? u.id ?? u.username ?? null) : null;
};
let lastUser = userKey(store.getState());
setDraftOwner(lastUser);
store.subscribe(() => {
    const now = userKey(store.getState());
    if (now === lastUser) return;
    lastUser = now;
    setDraftOwner(now);
});
